import { useState, useCallback } from "react";

// ponytail: constraint is `object` not `Record<string, unknown>` — plain caller
// interfaces (incl. those in the frozen test files) lack an index signature and
// would fail the `unknown` record constraint. `object` accepts them and is not `any`.
export interface UseFiltersOptions<T extends object> {
  initialValues: T;
  onFiltersChange?: (_filters: T) => void;
}

export interface UseFiltersReturn<T extends object> {
  filters: T;
  updateFilter: (_key: keyof T, _value: T[keyof T]) => void;
  resetFilters: () => void;
  setFilters: (_filters: Partial<T>) => void;
}

export const useFilters = <T extends object>({
  initialValues,
  onFiltersChange,
}: UseFiltersOptions<T>): UseFiltersReturn<T> => {
  const [filters, setFiltersState] = useState<T>(initialValues);

  const updateFilter = useCallback(
    (_key: keyof T, _value: T[keyof T]) => {
      const newFilters = { ...filters, [_key]: _value };
      setFiltersState(newFilters);
      onFiltersChange?.(newFilters);
    },
    [filters, onFiltersChange]
  );

  const resetFilters = useCallback(() => {
    setFiltersState(initialValues);
    onFiltersChange?.(initialValues);
  }, [initialValues, onFiltersChange]);

  const setFilters = useCallback(
    (_newFilters: Partial<T>) => {
      const updatedFilters = { ...filters, ..._newFilters };
      setFiltersState(updatedFilters);
      onFiltersChange?.(updatedFilters);
    },
    [filters, onFiltersChange]
  );

  return {
    filters,
    updateFilter,
    resetFilters,
    setFilters,
  };
};

export default useFilters;
